/**
 * O CELULAR QUE ABRE A TELA DE COMPUTADOR vai para o app (decisão 49).
 *
 * O endereço do sistema é um só: quem o abre no telefone cai em `/m`, na tela
 * feita para o telefone, e na tela equivalente quando ela existe. Quem quiser
 * a tela de computador no celular abre com `?mesa=1`, e a escolha vale SÓ
 * PARA AQUELA ABA (`sessionStorage`).
 *
 * Ela já foi guardada no aparelho para sempre (`localStorage`), e foi um
 * erro: quem tocou uma vez num módulo do Mais que abria a mesa ficou preso na
 * mesa em todo endereço, inclusive no `/login`, sem saber por quê. Por isso a
 * marca antiga é apagada ao ler.
 *
 * A administração da plataforma tem tela no app desde o M43 (05/10/2026):
 * `/plataforma` vai para `/m/admin/plataforma`, e o primeiro acesso de
 * plataforma (o convite) para `/m/plataforma/primeiro-acesso` — o código vem
 * no `#`, que o `main.tsx` preserva ao trocar de endereço. Com a paridade mesa
 * × celular (05/10/2026), criar empresa e configurar a IA também têm tela no
 * app; o `?mesa=1` fica só para quem prefere a tela de computador.
 */

const CHAVE = 'ct-frotas:usar-mesa-no-celular';

/** A tela do app que corresponde à da mesa. O resto abre o Início do app. */
const NO_APP: readonly (readonly [RegExp, string])[] = [
  [/^\/entrar\/?$/, '/m/entrar'],
  [/^\/login\/?$/, '/m/entrar'],
  [/^\/primeiro-acesso\/?$/, '/m/primeiro-acesso'],
  [/^\/plataforma\/primeiro-acesso\/?$/, '/m/plataforma/primeiro-acesso'],
  [/^\/equipamentos\/?$/, '/m/frota'],
  [/^\/equipamentos\/novo\/?$/, '/m/frota/novo'],
  [/^\/equipamentos\/importar\/?$/, '/m/frota/importar'],
  [/^\/equipamentos\/catalogo\/?$/, '/m/frota/catalogo'],
  [/^\/combustivel\/lancamento\/?$/, '/m/lancar/abastecimento'],
  [/^\/combustivel\/folhas\/?$/, '/m/lancar/folha'],
  [/^\/combustivel\/ordens\/?$/, '/m/combustivel/ordens'],
  [/^\/combustivel\/cobrancas\/?$/, '/m/combustivel/cobrancas'],
  [/^\/combustivel\/consumo\/?$/, '/m/combustivel/consumo'],
  [/^\/combustivel\/cadastros\/?$/, '/m/combustivel/cadastros'],
  [/^\/combustivel(\/tanques)?\/?$/, '/m/combustivel'],
  [/^\/operacao\/folhas\/?$/, '/m/operacao/folhas'],
  [/^\/operacao\/conciliacao\/?$/, '/m/operacao/conciliacao'],
  [/^\/operacao\/importacoes\/?$/, '/m/operacao/importacoes'],
  /* Na mesa, `/operacao/cadastros` é o catálogo das causas de parada. */
  [/^\/operacao\/cadastros\/?$/, '/m/operacao/causas'],
  [/^\/inspecao\/checklists\/?$/, '/m/inspecao/checklists'],
  [/^\/inspecao\/?$/, '/m/inspecao'],
  [/^\/cadastros\/governanca\/?$/, '/m/admin/pessoas'],
  [/^\/cadastros\/trilha\/?$/, '/m/admin/trilha'],
  [/^\/cadastros\/?$/, '/m/admin/pessoas'],
  [/^\/plataforma\/?$/, '/m/admin/plataforma'],
  [/^\/painel\/gestor\/?$/, '/m/gestor'],
  [/^\/painel\/ia\/?$/, '/m/ia'],
  [/^\/perfil\/?$/, '/m/perfil'],
  [/^\/ordens\/?$/, '/m/oficina'],
  /* Na mesa, `/manutencao` junta os vencimentos e os planos; no app, a tela
     dos vencimentos é a porta, e os planos ficam um toque adiante. */
  [/^\/manutencao\/?$/, '/m/manutencao'],
  [/^\/operacao\/?$/, '/m/operacao'],
  [/^\/estoque\/?$/, '/m/estoque'],
  [/^\/compras\/?$/, '/m/compras'],
  [/^\/custos\/?$/, '/m/custos'],
  [/^\/receita\/?$/, '/m/receita'],
  [/^\/pessoas\/?$/, '/m/documentos'],
  [/^\/tecnico\/?$/, '/m/biblioteca'],
];

/**
 * O TABLET ANDROID TAMBÉM VAI PARA O APP (decisão 65, 31/10/2026). Ele ficava
 * na mesa, e a mesa não abre sem sinal: o service worker vive em `/m/`, e é só
 * o app que guarda o pacote do campo e a fila do que se lança sem rede. No
 * pátio, o tablet do apontador era o aparelho que mais precisava disso. O
 * agente do tablet Android não tem "Mobile"; por isso a regra é "Android", e
 * não "Android.*Mobile". Quem quiser a tela de computador nele abre com
 * `?mesa=1`, como no telefone. O iPad continua na mesa: o pedido foi do
 * tablet Android, e o Safari do iPad se anuncia como Mac.
 */
export function ehTelefone(userAgent: string): boolean {
  return /iPhone|iPod|Android/i.test(userAgent);
}

/**
 * Para onde o telefone vai, ou `null` para ficar na mesa. Pura, para o teste:
 * quem chama passa o endereço, o agente do navegador e se a pessoa escolheu a mesa.
 */
export function destinoNoCelular(caminho: string, userAgent: string, escolheuMesa: boolean): string | null {
  if (!ehTelefone(userAgent) || escolheuMesa) return null;
  if (caminho.startsWith('/m/') || caminho === '/m') return null;
  for (const [padrao, destino] of NO_APP) if (padrao.test(caminho)) return destino;
  return '/m/';
}

/** Lê e grava a escolha da mesa na aba; `?mesa=1` liga, `?mesa=0` desliga. */
export function escolhaDaMesa(busca: string): boolean {
  const pedido = new URLSearchParams(busca).get('mesa');
  try {
    window.localStorage.removeItem(CHAVE);
  } catch {
    /* sem acesso ao armazenamento: não havia o que apagar */
  }
  try {
    if (pedido === '1') window.sessionStorage.setItem(CHAVE, '1');
    if (pedido === '0') window.sessionStorage.removeItem(CHAVE);
    return window.sessionStorage.getItem(CHAVE) === '1';
  } catch {
    return pedido === '1';
  }
}
